import React, { useState, useEffect } from 'react';
import * as env from 'config/environment';
import * as api from 'config/api';
import {
  getUser,
  getNotificationSetting as getNotificationSettingAPI,
  GetNotificationSettingResult,
  updateNotificationSetting,
  UpdateNotificationSetting,
} from 'config/api';
import { useRouting } from 'hooks/useRouting';
import { useScroll } from 'hooks/useScroll';
import { updateRegisteredWebPush, updateViewedWebPush } from 'utils/storage';
import { getUserStorage } from 'components/features/utils/utils';
import Loading from 'components/commons/loading/Loading';
import { isPremium, isIosApp } from 'components/features/utils/userStatus';

const NotificationSetting: React.FC = () => {
  const routing = useRouting();
  const user = getUserStorage();
  const initialValue = user !== null && user.RegisteredWebPush;

  const [pushNotification, setPushNotification] = useState<boolean>(
    initialValue
  );

  const [notificationSetting, setNotificationSetting] = useState<
    GetNotificationSettingResult | undefined
  >();
  const [linkedLine, setLinkedLine] = useState<boolean | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const userAgent = window.navigator.userAgent.toLowerCase();
  const isiPhone = /iphone|ipod/.test(userAgent);
  const isiPad = /ipad|macintosh/.test(userAgent) && 'ontouchend' in document;
  const isIos = isiPhone || isiPad;

  useEffect(() => {
    (async () => {
      const resNotificationSetting = await getNotificationSettingAPI();
      setNotificationSetting(resNotificationSetting);
      const resUser = await getUser();
      setLinkedLine(resUser.LinkedLine);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useScroll();

  if (!notificationSetting || linkedLine === null)
    return (
      <Loading
        loading={true}
        style={{
          backgroundColor: '#edf2f7',
          alignItems: 'normal',
          paddingTop: '200px',
          top: '95',
          left: '0',
        }}
      />
    );

  const receiveNotifications = async () => {
    if (!('Notification' in window)) {
    } else {
      await window.Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          // ブラウザポップアップで許可時の処理
          if (!('serviceWorker' in navigator)) {
          } else {
            navigator.serviceWorker.ready.then(registration => {
              registration.pushManager
                .subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: env.webPushPublickKey,
                })
                // TODO: 適切な型があれば変える
                .then((pushSubscription: { toJSON: () => any }) => {
                  const subscriptionJSON = pushSubscription.toJSON();

                  api
                    .RegisterUserWebPushInfo({
                      endPoint: subscriptionJSON.endpoint ?? '',
                      publicKey: subscriptionJSON.keys
                        ? subscriptionJSON.keys.p256dh
                        : '',
                      authInfo: subscriptionJSON.keys
                        ? subscriptionJSON.keys.auth
                        : '',
                    })
                    .then(ret => {
                      if (!ret.Success) {
                        alert(`通信に失敗しました、再度お試しください`);
                      }

                      updateViewedWebPush(true, true);
                      updateRegisteredWebPush(true);
                      setPushNotification(true);
                    });
                });
            });
          }
        }
      });
    }
  };

  // トグル押下
  const onClickPushNotification = async () => {
    if (isLoading) return;
    setIsLoading(true);

    if (pushNotification) {
      await api.UnregisterUserWebPushInfo();
      updateViewedWebPush(true, true);
      updateRegisteredWebPush(false);

      setPushNotification(prev => !prev);
    } else {
      if (!isIos) {
        await receiveNotifications();
      }
    }
    setIsLoading(false);
  };

  const goToTimelyDisclosure = (): void => {
    routing.pushRouting({ path: '/timelyDisclosure' }, true, window.scrollY);
  };

  const goToAsstsChangeNotification = (): void => {
    routing.pushRouting(
      { path: '/asstsChangeNotification' },
      true,
      window.scrollY
    );
  };

  const onClickEconomicIndicatorsOnToday = async (id: number) => {
    setNotificationSetting(
      prevState =>
        ({
          ...prevState,
          EconomicIndicatorsOnToday:
            prevState !== undefined && !prevState.EconomicIndicatorsOnToday,
        } as GetNotificationSettingResult)
    );

    await registNotificationSetting(
      id,
      !notificationSetting.EconomicIndicatorsOnToday
    );
  };

  const onClickNewDepositAndWithdrawal = async (id: number) => {
    setNotificationSetting(
      prevState =>
        ({
          ...prevState,
          NewDepositAndWithdrawal:
            prevState !== undefined && !prevState.NewDepositAndWithdrawal,
        } as GetNotificationSettingResult)
    );
    await registNotificationSetting(
      id,
      !notificationSetting.NewDepositAndWithdrawal
    );
  };

  const onClickNewTrade = async (id: number) => {
    setNotificationSetting(
      prevState =>
        ({
          ...prevState,
          NewTrade: prevState !== undefined && !prevState.NewTrade,
        } as GetNotificationSettingResult)
    );
    await registNotificationSetting(id, !notificationSetting.NewTrade);
  };

  const chkNotifications = [
    {
      id: 1,
      title: '保有銘柄の適時開示',
      message: <>保有銘柄の適時開示情報をお知らせします</>,
      explanation: <></>,
      goNext: goToTimelyDisclosure,
      isIosShow: true,
    },
    {
      id: 2,
      title: '資産変動のお知らせ',
      message: <>大きく資産が変動した時にお知らせします</>,
      explanation: (
        <>
          日次レポートにお知らせが表示されます
          <br />
          「LINEで受け取る」設定中はLINEでも受け取れます
        </>
      ),
      goNext: goToAsstsChangeNotification,
      isIosShow: true,
    },
    {
      id: 3,
      title: '本日の経済指標',
      message: <>朝、重要な経済指標をお知らせします</>,
      explanation: <>「LINEで受け取る」設定中はLINEでも受け取れます</>,
      status: notificationSetting.EconomicIndicatorsOnToday,
      onChange: onClickEconomicIndicatorsOnToday,
      isIosShow: false,
    },
    {
      id: 4,
      title: '入出金レポート',
      message: (
        <>
          最近の入出金をまとめてチェックできます。
          <br />
          ※MoneyTree連携口座がレポート対象です
        </>
      ),
      explanation: <>「LINEで受け取る」設定中はLINEでも受け取れます</>,
      status: notificationSetting.NewDepositAndWithdrawal,
      onChange: onClickNewDepositAndWithdrawal,
      isIosShow: false,
    },
    {
      id: 5,
      title: '取引レポート',
      message: <>最近の取引履歴をまとめてチェックできます。</>,
      explanation: <>「LINEで受け取る」設定中はLINEでも受け取れます</>,
      status: notificationSetting.NewTrade,
      onChange: onClickNewTrade,
      isIosShow: false,
    },
    // {
    //   id: 6,
    //   title: '配当金レポート',
    //   message: <>配当金が入金されると通知されます</>,
    //   explanation: (
    //     <>
    //       ・アセプラのお知らせへ通知
    //       <br />
    //       ・LINEへ通知（友達追加が必要です）
    //     </>
    //   ),
    // },
  ];

  const registNotificationSetting = async (
    id: number,
    isReceiveNotification: boolean
  ) => {
    const prams = {
      TimelyDisclosureBySymbol: notificationSetting.TimelyDisclosureBySymbol,
      AssetFluctuationThresholdPer:
        notificationSetting.AssetFluctuationThresholdPer,
      EconomicIndicatorsOnToday:
        id === 3
          ? isReceiveNotification
          : notificationSetting.EconomicIndicatorsOnToday,
      NewDepositAndWithdrawal:
        id === 4
          ? isReceiveNotification
          : notificationSetting.NewDepositAndWithdrawal,
      NewTrade: id === 5 ? isReceiveNotification : notificationSetting.NewTrade,
    } as UpdateNotificationSetting;

    await updateNotificationSetting(prams);
  };

  const chkView = chkNotifications.map((item, index) => {
    if (isIosApp() && !item.isIosShow) return <></>;

    const borderClass =
      index === 0 ? 'border-l border-r' : 'border-t border-l border-r';
    return (
      <div key={index}>
        {item.id === 1 || item.id === 2 ? (
          <div
            className="p-4 grid border-b border-bluegray-200 cursor-pointer"
            style={{ gridTemplateColumns: '1fr auto' }}
            onClick={(): void => {
              if (isLoading) return;
              item.goNext && item.goNext();
            }}>
            <div className="">
              <div className="text-base font-bold">{item.title}</div>
              <div className="text-xs font-bold mt-1">{item.message}</div>
              <div className="text-xs mt-1 text-bluegray-500">
                {item.explanation}
              </div>
            </div>
            <div className={`flex items-center`}>
              <div
                className={`icon-chevron-right text-2xl text-gray-500`}></div>
            </div>
          </div>
        ) : (
          <div
            className="p-4 grid border-b border-bluegray-200"
            style={{ gridTemplateColumns: '1fr auto' }}>
            <div className="">
              <div className="text-base font-bold">{item.title}</div>
              <div className="text-xs font-bold mt-1">{item.message}</div>
              <div className="text-xs mt-1 text-bluegray-500">
                {item.explanation}
              </div>
            </div>
            {!isPremium() ? (
              <div className={`flex flex-col justify-center items-center`}>
                <div className={`icon-lock text-lock text-xl`}></div>
                <div
                  className={`mt-0.5 text-bluegray-400 text-1.5xs text-center`}>
                  プレミアム
                  <br />
                  会員限定
                </div>
              </div>
            ) : (
              <div className="flex items-center">
                <div className="toggle ml-2">
                  <div className="checkbox">
                    <input
                      className="toggle-lg"
                      id={`${index}`}
                      type="checkbox"
                      checked={item.status}
                      onChange={() => {
                        if (isLoading) return;
                        item.onChange && item.onChange(item.id);
                      }}
                    />
                    <label className="toggle-lg" htmlFor={``}></label>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  });

  const lineText = () => {
    return (
      <div>
        <div className="text-base font-bold">LINEで受け取る</div>
        <div className="text-xs text-bluegray-500">
          ※通知の解除はLINE上からお願いします
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="mt-8 px-4 font-bold">通知の受け取り方の設定</div>
      <div className="px-4 pb-2 text-xs text-bluegray-500">
        アセプラにログインしていなくても、自動的にお知らせします。
      </div>
      {linkedLine ? (
        <div
          className="p-4 grid bg-white border-b border-bluegray-200"
          style={{ gridTemplateColumns: '1fr auto' }}>
          {lineText()}
          <div className="flex items-center">
            <div className="text-xs text-teal-500">設定中</div>
          </div>
        </div>
      ) : (
        <a id="notificationSetting_line" href={env.lineAddFriendURL}>
          <div
            className="p-4 grid items-center bg-white border-b border-bluegray-200"
            style={{ gridTemplateColumns: '1fr auto' }}>
            {lineText()}
            <div className="icon-chevron-right text-2xl text-gray-500 ml-auto"></div>
          </div>
        </a>
      )}
      {!isIos && (
        <div
          className={`p-4 grid bg-white border-b border-bluegray-200`}
          style={{ gridTemplateColumns: '1fr auto' }}>
          <div className="font-bold">
            <div className="text-base">端末へのプッシュ通知で受け取る</div>
            <div className="text-11">(Webプッシュ通知)</div>
          </div>
          <div className="flex items-center">
            <div className="toggle ml-2">
              <div className="checkbox">
                <input
                  className="toggle-lg"
                  id={``}
                  type="checkbox"
                  checked={pushNotification}
                  onChange={() => onClickPushNotification()}
                />
                <label className="toggle-lg" htmlFor={``}></label>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mt-8 px-4 font-bold">受け取る内容の設定</div>
      <div className="px-4 pb-2 text-xs text-bluegray-500">
        設定した内容はアセプラ内の「お知らせ」へ通知します。
      </div>
      <div className="bg-white">{chkView}</div>
      {!isIosApp() && (
        <>
          {!isPremium() ? (
            <div className={`mt-6 mx-2 px-4 py-4 bg-blue-925 rounded-lg`}>
              <div className={`flex flex-row items-center justify-center`}>
                <div>
                  <img
                    src={`/static/images/premium-notification.svg`}
                    alt={`お知らせ通知`}
                    className={``}
                  />
                </div>
                <div className={`ml-2 text-sm font-bold`}>
                  <div className={`text-yellow-350`}>
                    みんかぶプレミアムに入会で
                    <br />
                    通知機能強化でもっと便利！
                  </div>
                  <div className={`flex flex-row items-center mt-1`}>
                    <div
                      className={`icon-check text-yellow-350 font-bold`}></div>
                    <div className={`ml-1 text-white`}>
                      お知らせの通知機能強化
                    </div>
                  </div>
                  <div className={`flex flex-row items-center mt-1`}>
                    <div
                      className={`icon-check text-yellow-350 font-bold`}></div>
                    <div className={`ml-1 text-white`}>LINEへ通知</div>
                  </div>
                  <div className={`text-white text-xs`}>
                    　　（一部通知対象外）
                  </div>
                </div>
              </div>
              <div className={`pt-4`}>
                <a
                  href={`${env.premiumURL}?service=${
                    env.serviceName
                  }&utm_source=tw_minkabu&utm_medium=notificationSetting&utm_campaign=bn_notification&callback_url=${encodeURI(
                    `${env.appURL}/auth`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer">
                  <button
                    className={`bg-tradewell rounded-lg shadow text-white py-2 text-center flex flex-row items-center gap-2 justify-center text-base font-bold w-full`}>
                    初回30日間無料でお試し
                  </button>
                </a>
              </div>
            </div>
          ) : (
            <>
              {!linkedLine ? (
                <div className="w-full mt-6">
                  <a id="notificationSetting_line" href={env.lineAddFriendURL}>
                    <img
                      src="/static/images/line2.png"
                      alt="友だち追加"
                      className="mx-auto mb-2 px-4"
                    />
                  </a>
                </div>
              ) : (
                <></>
              )}
            </>
          )}
        </>
      )}
      {/* <WhatIsIphoneAddIconModal {...{ hide: hideModal, isShow: isShowModal }} /> */}
    </>
  );
};

export default NotificationSetting;
